import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, GraduationCap, Sparkles, UserRound } from "lucide-react";
import {
  studentConversationsKey,
  studentMessagingApi,
  type StudentConversation,
  type StudentConversationKind,
  type StudentMessage,
} from "../../api/student-messaging.api";
import {
  AI_REPLY_MENTION,
  STUDENT_KIND_LABEL,
  STUDENT_MARK,
  STUDENT_MAX_BODY,
  STUDENT_REFRESH_MS,
  STUDENT_ROLE_LABEL,
  initials,
  isRefusal,
  mergeMessages,
  studentErrorMessage,
  studentNotice,
} from "./student-shared";
import { StudentDraft } from "./StudentDraft";
import { Rephrase } from "./Rephrase";
import { PresenceDot, PresenceLabel, PresenceText } from "./presence";
import { CandidatePhoto, StaffPhoto } from "../photos/ProfilePhotos";
import { useAuthStore } from "../../store/auth.store";
import {
  BackButton,
  CONTINUED_MS,
  ConversationAvatar,
  useAutoGrow,
} from "./parts";
import "./inline-composer.css";

/** Marque d'une conversation lue par un ou des élèves (liste et en-tête du fil). */
export function StudentMark({ kind }: { kind: StudentConversationKind }) {
  return (
    <span className="messaging-student-mark">
      <GraduationCap size={12} aria-hidden="true" />
      {STUDENT_MARK[kind] ?? "Élève"}
    </span>
  );
}

const timeFormat = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});
const dayFormat = new Intl.DateTimeFormat("fr-FR", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Fil d'une conversation avec un ou des élèves, dans le volet Messagerie.
 * Texte seul : ni référence interne, ni pièce jointe, ni réaction, ni modification, ni tâche.
 * Pas de temps réel : le fil est relu toutes les 20 s.
 */
export function StudentThread({
  id,
  scope,
  summary,
  author,
  onBack,
}: {
  id: string;
  scope: string;
  /** Ligne de la liste, pour le titre avant la première réponse et le lien vers la fiche. */
  summary?: StudentConversation;
  /** Nom affiché sur un message tout juste envoyé, avant la relecture du fil. */
  author: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const listKey = useMemo(() => studentConversationsKey(scope), [scope]);
  const threadKey = useMemo(
    () => ["student-messaging-thread", scope, id] as const,
    [scope, id],
  );
  const latest = useQuery({
    queryKey: threadKey,
    queryFn: () => studentMessagingApi.messages(id),
    retry: false,
    refetchInterval: (query) =>
      isRefusal(query.state.error) ? false : STUDENT_REFRESH_MS,
  });
  const thread = latest.data;

  // Tout ce qui a été affiché reste affiché : pages précédentes, messages sortis de la dernière page.
  const [seen, setSeen] = useState<StudentMessage[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  useEffect(() => {
    if (!thread) return;
    setSeen((known) => mergeMessages(known, thread.items));
    setOlderHasMore((known) => known ?? thread.hasMore);
  }, [thread]);
  const messages = useMemo(
    () => mergeMessages(seen, thread?.items ?? []),
    [seen, thread],
  );
  const hasMore = olderHasMore ?? thread?.hasMore ?? false;
  const firstSequence = messages[0]?.sequence ?? 0;
  const lastSequence = messages.length
    ? messages[messages.length - 1].sequence
    : 0;
  const canWrite = thread?.conversation.canWrite ?? false;

  // ── Défilement : on reste en bas à l'arrivée d'un message, en place en remontant le fil. ──
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const anchor = useRef<number | null>(null);
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;
    if (anchor.current !== null) {
      element.scrollTop = element.scrollHeight - anchor.current;
      anchor.current = null;
    } else if (atBottom.current) element.scrollTop = element.scrollHeight;
  }, [firstSequence, lastSequence]);

  const older = useMutation({
    mutationFn: () => studentMessagingApi.messages(id, firstSequence),
    onSuccess: (page) => {
      const element = scroller.current;
      if (element) anchor.current = element.scrollHeight - element.scrollTop;
      setSeen((known) => mergeMessages(page.items, known));
      setOlderHasMore(page.hasMore);
    },
  });

  // ── Lecture : jusqu'au dernier message affiché, jamais pour un compte en lecture seule. ──
  const marked = useRef(0);
  useEffect(() => {
    const mark = () => {
      if (
        !canWrite ||
        lastSequence <= marked.current ||
        document.visibilityState !== "visible"
      )
        return;
      const previous = marked.current;
      marked.current = lastSequence;
      studentMessagingApi.read(id, lastSequence).then(
        () => void queryClient.invalidateQueries({ queryKey: listKey }),
        // Sans gravité : le « non lu » reste affiché, la lecture sera retentée au prochain message.
        () => {
          if (marked.current === lastSequence) marked.current = previous;
        },
      );
    };
    mark();
    document.addEventListener("visibilitychange", mark);
    return () => document.removeEventListener("visibilitychange", mark);
  }, [canWrite, id, lastSequence, listKey, queryClient]);

  // ── Envoi : le même `requestId` tant que le texte ne change pas (nouvel essai sans doublon). ──
  const [draft, setDraft] = useState("");
  const composer = useRef<HTMLFormElement>(null);
  useAutoGrow(composer, draft);
  const attempt = useRef<{ body: string; requestId: string } | null>(null);
  const send = useMutation({
    mutationFn: (body: string) => {
      if (attempt.current?.body !== body)
        attempt.current = { body, requestId: crypto.randomUUID() };
      return studentMessagingApi.send(id, attempt.current.requestId, body);
    },
    onSuccess: (sent, body) => {
      attempt.current = null;
      // L'envoi vaut lecture côté serveur : pas d'appel de lecture pour son propre message.
      marked.current = Math.max(marked.current, sent.sequence);
      atBottom.current = true;
      setDraft("");
      setSeen((known) =>
        mergeMessages(known, [
          {
            id: sent.id,
            sequence: sent.sequence,
            mine: true,
            authorKind: "staff",
            author,
            body,
            createdAt: new Date().toISOString(),
          },
        ]),
      );
      void queryClient.invalidateQueries({ queryKey: threadKey });
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Entrée envoie ; Maj+Entrée va à la ligne ; une saisie en cours de composition n'envoie rien.
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    )
      return;
    event.preventDefault();
    submit();
  };

  const kind = thread?.conversation.kind ?? summary?.kind;
  const me = useAuthStore((s) => s.user?.id);
  // Boîte du bureau : Permio prépare une réponse quand le dernier message vient de l'élève ;
  // un accusé de réception de l'assistant ne compte pas comme une réponse du bureau.
  const last = [...messages]
    .reverse()
    .find((m) => m.authorKind !== "assistant");
  const awaiting =
    kind === "OFFICE" && last?.authorKind === "student" && !last.mine
      ? last.sequence
      : null;
  const title = thread?.conversation.title ?? summary?.title ?? "Conversation";
  const presence = thread?.conversation.presence ?? summary?.presence;
  const candidateId = summary?.candidateId;

  return (
    <section className="messaging-conversation messaging-student-thread">
      <header className="messaging-conv-head">
        <BackButton onBack={onBack} />
        <ConversationAvatar
          kind="student"
          name={title}
          candidateId={kind !== "GROUP" ? candidateId : null}
          presence={presence}
        />
        <div className="messaging-conv-title">
          <h2>{title}</h2>
          <p className="messaging-conv-sub">
            {kind && <StudentMark kind={kind} />}
            {kind && (
              <span
                title={
                  kind === "OFFICE"
                    ? "Boîte commune : tout le secrétariat lit et répond ici."
                    : undefined
                }
              >
                {STUDENT_KIND_LABEL[kind]}
              </span>
            )}
            <PresenceLabel presence={presence} />
          </p>
        </div>
        {kind !== "GROUP" && candidateId && (
          <div className="messaging-conv-actions">
            <Link
              className="messaging-pill-link"
              to={`/candidates/${candidateId}`}
            >
              <UserRound size={15} aria-hidden="true" />
              Ouvrir la fiche
            </Link>
          </div>
        )}
      </header>
      {kind === "GROUP" && !!thread?.members.length && (
        <ul className="messaging-members-strip" aria-label="Membres du groupe">
          {thread.members.map((member, index) => (
            <li
              key={index}
              className={
                member.kind === "student"
                  ? "messaging-student-member"
                  : undefined
              }
            >
              <PresenceDot presence={member.presence} />
              {member.name}
              <PresenceText presence={member.presence} />
              <span>
                {" · "}
                {STUDENT_ROLE_LABEL[member.role] ??
                  (member.kind === "student" ? "Élève" : "Équipe")}
              </span>
            </li>
          ))}
        </ul>
      )}

      {latest.isError && !thread ? (
        <div className="messaging-state-box">
          <p role="alert">
            {studentErrorMessage(
              latest.error,
              "Conversation indisponible. Vérifiez la connexion puis réessayez.",
            )}
          </p>
          <div className="messaging-state-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => void latest.refetch()}
            >
              Réessayer
            </button>
          </div>
        </div>
      ) : !thread ? (
        <p role="status" className="messaging-state-box">
          Chargement…
        </p>
      ) : (
        <>
          {latest.isError && (
            <p
              role="status"
              className="messaging-banner messaging-banner--warn"
            >
              <span>
                {studentErrorMessage(latest.error, "Actualisation impossible.")}
              </span>{" "}
              <button type="button" onClick={() => void latest.refetch()}>
                Réessayer
              </button>
            </p>
          )}
          <div
            ref={scroller}
            role="log"
            aria-label="Messages"
            tabIndex={0}
            className="messaging-student-log messaging-timeline"
            onScroll={(event) => {
              const element = event.currentTarget;
              atBottom.current =
                element.scrollHeight -
                  element.scrollTop -
                  element.clientHeight <
                80;
            }}
          >
            {hasMore && (
              <div className="messaging-history-nav">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={older.isPending}
                  onClick={() => older.mutate()}
                >
                  Messages précédents
                </button>
              </div>
            )}
            {older.isError && (
              <p role="alert" className="messaging-error">
                {studentErrorMessage(
                  older.error,
                  "Messages précédents indisponibles.",
                )}
              </p>
            )}
            {messages.length === 0 ? (
              <p className="messaging-timeline-empty">
                Aucun message pour le moment.
              </p>
            ) : (
              <ol className="messaging-messages">
                {messages.map((message, index) => {
                  const at = new Date(message.createdAt);
                  const previous = messages[index - 1];
                  const newDay =
                    !previous ||
                    new Date(previous.createdAt).toDateString() !==
                      at.toDateString();
                  // Suite d'un message de la même personne : ni avatar ni nom, l'heure au survol.
                  // Une réponse de Permio AI n'est jamais regroupée : chacune porte la mention.
                  const continued =
                    !newDay &&
                    previous.mine === message.mine &&
                    previous.authorKind === message.authorKind &&
                    previous.author === message.author &&
                    message.authorKind !== "assistant" &&
                    at.getTime() - Date.parse(previous.createdAt) <
                      CONTINUED_MS;
                  const fromStudent =
                    !message.mine && message.authorKind === "student";
                  const fromAssistant =
                    !message.mine && message.authorKind === "assistant";
                  return (
                    <li
                      key={message.sequence}
                      className={
                        "messaging-message" +
                        (message.mine ? " messaging-message--mine" : "") +
                        (fromStudent ? " messaging-message--student" : "") +
                        (fromAssistant ? " messaging-message--assistant" : "") +
                        (continued ? " messaging-message--continued" : "")
                      }
                    >
                      {newDay && (
                        <div className="messaging-day">
                          <time dateTime={message.createdAt}>
                            {dayFormat.format(at)}
                          </time>
                        </div>
                      )}
                      <div className="messaging-message-main">
                        {continued ? (
                          <time
                            className="messaging-continued-time"
                            dateTime={message.createdAt}
                            title={at.toLocaleString("fr-FR")}
                          >
                            {timeFormat.format(at)}
                          </time>
                        ) : (
                          <div className="messaging-author-line">
                            <span
                              className="messaging-message-avatar"
                              aria-hidden="true"
                            >
                              {fromAssistant ? (
                                <Sparkles size={14} />
                              ) : (
                                initials(message.mine ? author : message.author)
                              )}
                              {message.mine ? (
                                <StaffPhoto
                                  userId={me}
                                  className="messaging-avatar-photo"
                                />
                              ) : (
                                fromStudent &&
                                kind !== "GROUP" && (
                                  <CandidatePhoto
                                    candidateId={candidateId}
                                    className="messaging-avatar-photo"
                                  />
                                )
                              )}
                            </span>
                            <strong>
                              {message.mine
                                ? "Vous"
                                : fromAssistant
                                  ? AI_REPLY_MENTION
                                  : message.author}
                            </strong>{" "}
                            {!message.mine && !fromAssistant && (
                              <small>· {fromStudent ? "Élève" : "Équipe"}</small>
                            )}
                            <time
                              dateTime={message.createdAt}
                              title={at.toLocaleString("fr-FR")}
                            >
                              {timeFormat.format(at)}
                            </time>
                          </div>
                        )}
                        <p className="messaging-text">{message.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {canWrite ? (
            <form
              ref={composer}
              className="messaging-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              {send.isError && (
                <p role="alert" className="messaging-error">
                  {studentErrorMessage(
                    send.error,
                    "Envoi impossible. Votre message est conservé.",
                  )}
                </p>
              )}
              {kind === "OFFICE" && (
                <StudentDraft
                  conversationId={id}
                  sequence={awaiting}
                  replacing={!!draft.trim()}
                  onUse={(body) => {
                    if (send.isError) send.reset();
                    setDraft(body);
                  }}
                />
              )}
              <Rephrase
                text={draft}
                disabled={send.isPending}
                onUse={(text) => {
                  if (send.isError) send.reset();
                  setDraft(text);
                }}
              />
              <div className="inline-composer">
                <label>
                  <span className="messaging-sr-only">Votre message</span>
                  <textarea
                    aria-label="Votre message"
                    placeholder={
                      kind === "GROUP"
                        ? "Écrire au groupe…"
                        : `Écrire à ${title}…`
                    }
                    rows={3}
                    maxLength={STUDENT_MAX_BODY}
                    value={draft}
                    readOnly={send.isPending}
                    onChange={(event) => {
                      if (send.isError) send.reset();
                      setDraft(event.target.value);
                    }}
                    onKeyDown={onKeyDown}
                  />
                </label>
                <div className="inline-composer-tools">
                  <span className="inline-send-action">
                    <button
                      type="submit"
                      className="messaging-send"
                      aria-label="Envoyer"
                      title="Envoyer (Entrée)"
                      disabled={!draft.trim() || send.isPending}
                    >
                      <ArrowUp size={18} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </span>
                  <small>
                    Entrée pour envoyer · Maj + Entrée pour une ligne · texte
                    seul
                  </small>
                </div>
              </div>
              <p className="messaging-composer-note">
                <GraduationCap size={14} aria-hidden="true" />
                {studentNotice(kind)}
              </p>
            </form>
          ) : (
            <div className="messaging-student-readonly">
              <p>Compte en lecture seule</p>
              <p className="messaging-composer-note">
                <GraduationCap size={14} aria-hidden="true" />
                {studentNotice(kind)}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
